import {
  CountryStripePriceInfo,
  CountryStripeProductInfo,
} from "@/src/features/pricing/types";
import { useMemo } from "react";
import useSWR, { SWRConfiguration } from "swr";

export type CountryStripeProductData = CountryStripeProductInfo;

// Custom Error for Fetching Product Data
export class FetchProductError extends Error {
  status?: number;
  info?: unknown;

  constructor(message: string, status?: number, info?: unknown) {
    super(message);
    this.name = "FetchProductError";
    this.status = status;
    this.info = info;
  }
}

interface UseCountryProductReturn {
  productData: CountryStripeProductData | undefined;
  prices: CountryStripePriceInfo[] | undefined;
  yearlyPrice: CountryStripePriceInfo | undefined;
  computedSubheading: string | undefined;
  computedFooter: string | undefined;
  isLoading: boolean;
  error: FetchProductError | Error | unknown;
}

// Standard fetcher function for SWR, updated type annotation and error handling
const fetcher = async (url: string): Promise<CountryStripeProductData> => {
  const res = await fetch(url);
  if (!res.ok) {
    let errorInfo: unknown = {};
    try {
      // Try to parse the error response body as JSON
      errorInfo = await res.json();
    } catch {
      // If parsing fails (e.g., empty body or not JSON), get text
      try {
        errorInfo = await res.text();
      } catch {
        // Fallback if getting text also fails
        errorInfo = "Failed to read error response body.";
      }
    }
    // Throw a custom error with more context
    throw new FetchProductError(
      `[useCountryStripeProduct] Failed to fetch country product data. Status: ${res.status}`,
      res.status,
      errorInfo
    );
  }

  // Check if the response body is likely empty or invalid JSON before parsing
  const contentType = res.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    // Handle cases where the response isn't JSON as expected
    const responseText = await res.text();
    console.warn(
      `[useCountryStripeProduct] Expected JSON response but received ${contentType}. Body: ${responseText.substring(
        0,
        100
      )}...`
    );
    // Depending on requirements, either throw or return a default/empty state
    throw new FetchProductError(
      `[useCountryStripeProduct] Invalid content type received: ${contentType}`,
      res.status,
      { responseText }
    );
  }

  try {
    // The API returns the full CountryProductInfo object
    return await res.json();
  } catch (parseError) {
    console.error(
      "[useCountryStripeProduct] Failed to parse successful JSON response:",
      parseError
    );
    throw new FetchProductError(
      "[useCountryStripeProduct] Failed to parse successful JSON response.",
      res.status,
      parseError // Include the parsing error details
    );
  }
};

/**
 * Hook to fetch the country-specific product (including prices) from the `/api/products/country` endpoint.
 * Returns the full product data and also extracts the prices array.
 * It uses SWR for caching and revalidation.
 */
export const useCountryStripeProduct = (
  swrOptions?: SWRConfiguration<CountryStripeProductData, FetchProductError>
): UseCountryProductReturn => {
  const API_ENDPOINT = "/api/products/country";

  const {
    data,
    error,
    isLoading: swrIsLoading,
  } = useSWR<CountryStripeProductData, FetchProductError>(
    API_ENDPOINT,
    fetcher,
    {
      revalidateOnFocus: false,
      ...swrOptions,
    }
  );

  const finalIsLoading = swrIsLoading;

  // Calculate yearlyPrice within the hook, since this is what contains the content-specific metadata
  const yearlyPrice: CountryStripePriceInfo | undefined = useMemo(() => {
    if (finalIsLoading || !data?.prices) return undefined;

    const yearlyPrice = data.prices.find(
      (p) => p.recurring?.interval === "year"
    );

    // We need to check that the price metadata is in the shape we're expecting, and issue a warning if it's not
    if (yearlyPrice?.metadata) {
      const metadata = yearlyPrice.metadata;
      // Explicitly define the expected keys as strings
      const expectedKeys: string[] = [
        "heading",
        "hero_footer",
        "hero_subheading",
        "module_price",
        "module_subheading",
        "subheading",
        "title",
        // "crossed_price", -- optional
        // "symbol", -- this is now derived
      ];
      const missingKeys: string[] = [];
      const incorrectTypeKeys: string[] = [];

      if (typeof metadata !== "object" || metadata === null) {
        console.warn(
          "[useCountryStripeProduct] Yearly price metadata is not an object or is null. Metadata:",
          metadata
        );
      } else {
        expectedKeys.forEach((key) => {
          if (!(key in metadata)) {
            missingKeys.push(key);
          } else if (typeof metadata[key] !== "string") {
            incorrectTypeKeys.push(key);
          }
        });

        if (missingKeys.length > 0) {
          console.warn(
            `[useCountryStripeProduct] Yearly price metadata is missing expected keys: ${missingKeys.join(
              ", "
            )}. Metadata:`,
            metadata
          );
        }
        if (incorrectTypeKeys.length > 0) {
          console.warn(
            `[useCountryStripeProduct] Yearly price metadata has keys with incorrect types (expected string): ${incorrectTypeKeys.join(
              ", "
            )}. Metadata:`,
            metadata
          );
        }
      }
    }

    return yearlyPrice;
  }, [data?.prices, finalIsLoading]);

  // Compute subheading and footer based on loading state and yearly price metadata
  const computedSubheading = finalIsLoading
    ? "Loading..."
    : yearlyPrice?.metadata?.hero_subheading || "";
  const computedFooter = finalIsLoading
    ? "Loading..."
    : yearlyPrice?.metadata?.hero_footer || "";

  return {
    productData: data,
    prices: data?.prices,
    yearlyPrice,
    computedSubheading,
    computedFooter,
    isLoading: finalIsLoading,
    error,
  };
};
